import {
  SHARE_INTEREST_MESSAGE,
  NEXT_STEP_MESSAGE,
  ASK_QUESTION_MESSAGE,
  formatInterestsList,
} from './helpers';
import { buildStepResponse } from './aiResponseProcessor';

// Generic Instructions
const OPTIONS_INSTRUCTION = 'Click an option below:';
export const AMENITY_INSTRUCTION = 'Click any amenity below:';

export const conversationSteps = {

  // 1. Initial Contact Form
  welcome_form: {
    id: 'welcome_form',
    type: 'bot_message_with_form',
    message: 'Hello! I am your TourGuide AI assistant. I’m here to help you explore [COMMUNITY_NAME] and tour amenities that match your interests. Along the way, favorite the amenities you love and I’ll put them in a personalized brochure for you to keep.<br /><br />To get started, please tell me a bit about yourself.',
    instructions: 'Complete form below:',
    formFields: [{
      name: 'firstName',
      label: 'First Name',
      type: 'text',
      required: true,
      placeholder: 'First Name *',
    },
    {
      name: 'lastName',
      label: 'Last Name',
      type: 'text',
      required: true,
      placeholder: 'Last Name *',
    },
    {
      name: 'email',
      label: 'Email Address',
      type: 'email',
      required: false,
      placeholder: 'Email Address *',
      requiredGroup: 'contact',
    },
    {
      name: 'phone',
      label: 'Phone Number',
      type: 'tel',
      required: false,
      placeholder: 'Phone Number *',
      requiredGroup: 'contact',
    }],
    submitButton: {
      text: 'Start Tour',
      action: 'submit_form',
      nextStep: 'interests_selection',
    },
    clearDataLink: {
      text: 'Not you? Click here to start fresh.',
      action: 'clear_saved_data',
    },
  },

  // 1b. Form Thank You
  form_thank_you: {
    id: 'form_thank_you',
    type: 'bot_message',
    message: 'Glad to meet you, [FIRST_NAME]!',
  },

  // 2. Interest Selection (pick 3)
  interests_selection: {
    id: 'interests_selection',
    type: 'bot_message_with_buttons',
    message: "Let's begin your tour by learning more about you and your interests! This will help me tailor your [COMMUNITY_NAME] tour experience.",
    instructions: 'Click 3 interests below:',
    buttons: [],
  },

  // 3. AI Amenity Results (based on selections)
  step4: {
    id: 'step4',
    type: 'bot_message_with_buttons',
    message: "[FIRST_NAME], here's a selection of tour stops I really think you'll enjoy. You can take your time visiting these amenities - I'll be here when you're ready to explore more.",
    instructions: AMENITY_INSTRUCTION,
    buttons: [],
  },

  // 3a. More places pagination prompt
  step_more_places: {
    instructions: OPTIONS_INSTRUCTION,
    buttons: [{ text: 'Share Another Interest', action: 'decline_more_places' },
      { text: 'Ask a Question', action: 'ask_question', nextStep: 'step_ask_question' }],
  },

  // Share another interest
  share_interest: {
    id: 'share_interest',
    type: 'bot_message',
    message: SHARE_INTEREST_MESSAGE,
    instructions: 'Type an interest below:',
  },

  // Ask a Question (free-text input)
  step_ask_question: {
    id: 'step_ask_question',
    type: 'bot_message',
    message: ASK_QUESTION_MESSAGE,
    instructions: 'Type your question below:',
  },

  // What next? prompt
  next_step: {
    id: 'next_step',
    type: 'bot_message_with_buttons',
    message: NEXT_STEP_MESSAGE,
    instructions: OPTIONS_INSTRUCTION,
    buttons: [{
      text: 'Share Another Interest',
      action: 'continue_tour',
      nextStep: 'share_interest',
    },
    {
      text: 'Ask a Question',
      action: 'ask_question',
      nextStep: 'step_ask_question',
    },
    {
      text: 'Or, End Guided Tour & View Brochure',
      action: 'end_exploration',
    }],
  },

  // Idle timeout prompt
  idle_timeout: {
    id: 'idle_timeout',
    type: 'bot_message_with_buttons',
    message: "Looks like you've taken a break, [FIRST_NAME]. Do you want to resume your guided tour?",
    instructions: OPTIONS_INSTRUCTION,
    buttons: [{
      text: 'Yes, Resume Guided Tour',
      action: 'idle_timeout_no',
    },
    {
      text: 'No, End Guided Tour & View Brochure',
      action: 'idle_timeout_yes',
    }],
  },

  // 7. Session ended (goodbye)
  session_ended: {
    id: 'session_ended',
    type: 'bot_message_with_buttons',
    message: "We'll end your guided tour, but feel free to return for another any time. Thanks for visiting [COMMUNITY_NAME]!",
    instructions: OPTIONS_INSTRUCTION,
    buttons: [],
    links: [
      { text: 'Hide Guided Tour & Explore on My Own', action: 'close_panel' },
      { text: 'Or, Start a New Guided Tour', action: 'restart_session' },
    ],
  },

};

// Initial conversation state
export const initialConversationState = {
  currentStep: 'welcome_form',
  selectedInterests: [],
  selectedAmenities: [],
  visitedAmenities: [],
  visitedExternalLocations: [],
  pendingExternalVisit: null,
  selectedFollowUpAction: null,
  userInfo: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    previousSession: false,
  },
  conversationData: {},
  formData: {},
  formErrors: {},
};

/**
 * Build the form thank you message.
 */
export const buildFormThankYouMessage = (firstName, config = {}) => {
  const step = conversationSteps.form_thank_you;
  const state = { selectedInterests: [], userInfo: { firstName } };
  return buildStepResponse(step, state, config, []);
};

/**
 * Build the share interest prompt message.
 */
export const buildShareInterestMessage = (config = {}) => {
  const step = conversationSteps.share_interest;
  const state = { selectedInterests: [], userInfo: {} };
  return buildStepResponse(step, state, config, []);
};

/**
 * Build the "next step" follow-up message.
 */
export const buildNextStepMessage = (firstName, config = {}) => {
  const step = conversationSteps.next_step;
  if (!step) return null;

  const state = { selectedInterests: [], userInfo: { firstName } };
  const response = buildStepResponse(step, state, config, []);
  response.stepType = 'next_step_prompt';
  return response;
};

export const buildMorePlacesMessage = (interests, firstName = '', config = {}) => {
  const response = buildNextStepMessage(firstName, config);
  if (!response) return null;
  const morePlacesStep = conversationSteps.step_more_places;
  const formatted = formatInterestsList(interests);
  response.buttons = [{ text: `Tour More ${formatted} Amenities`, instructions: OPTIONS_INSTRUCTION, action: 'show_more_places' },
    ...(morePlacesStep?.buttons || [])];
  if (morePlacesStep?.instructions) response.instructions = morePlacesStep.instructions;
  response.stepType = 'more_places_prompt';
  return response;
};

/**
 * Build the idle timeout prompt message.
 */
export const buildIdleTimeoutMessage = (firstName, config = {}) => {
  const step = conversationSteps.idle_timeout;
  const state = { selectedInterests: [], userInfo: { firstName } };
  return buildStepResponse(step, state, config, []);
};

/**
 * Build the session ended (goodbye) message.
 */
export const buildSessionEndedMessage = (config = {}) => {
  const step = conversationSteps.session_ended;
  const state = { selectedInterests: [], userInfo: {} };
  const response = buildStepResponse(step, state, config, []);
  response.links = step.links || [];
  return response;
};

export const BROCHURE_BUTTON_TEXT = 'View My Brochure';

export const BROCHURE_INTRO = "We've put everything you've saved into your own personalized interactive brochure.";

/**
 * Add the brochure sentence to a goodbye's text; MessageList renders the
 * copyable URL directly after it. Appended at most once.
 *
 * @param {string} text - The goodbye text as first rendered.
 * @returns {string} The text, ending with the brochure sentence.
 */
export const withBrochureIntro = (text) => {
  if (!text || text.includes(BROCHURE_INTRO)) return text;
  return `${text}<br /><br />${BROCHURE_INTRO}`;
};

/**
 * Add the brochure button to a goodbye message's buttons.
 *
 * The link arrives after the goodbye is on screen — the hash only comes back on
 * the session-complete response — so this patches the buttons rather than being
 * folded into buildSessionEndedMessage(). The URL is built by
 * buildBrochureUrl(), which is what constrains it to a slug and a hex digest.
 * It goes first, above the goodbye's links.
 *
 * @param {Array} buttons - The goodbye's buttons as first rendered.
 * @param {string} url - Brochure URL, or a falsy value to leave them alone.
 * @returns {Array} The buttons, with the brochure button prepended at most once
 *   (the abandon beacon can fire after a natural end with the same hash).
 */
export const withBrochureButton = (buttons, url) => {
  if (!url || !buttons || buttons.some((button) => button.action === 'view_brochure')) return buttons;
  return [{ text: BROCHURE_BUTTON_TEXT, action: 'view_brochure', href: url }, ...buttons];
};
